/**
 * Shared helpers for client-side text filters over an in-memory list.
 *
 * Semantics, in one place so the lists that use it can't drift apart: the input is split on
 * whitespace into terms, and a row matches when EVERY term appears as a substring of at least
 * one of its fields. Matching is case-insensitive, and empty or whitespace-only input means
 * "no filter" rather than "match nothing".
 */

/** Tokenize filter input into lowercase terms. Empty or whitespace-only input yields no terms. */
export function toFilterTerms(text: string | null | undefined): string[] {
    return (text ?? '').toLowerCase().split(/\s+/).filter(Boolean);
}

/**
 * True when every term appears somewhere in the supplied fields.
 *
 * Fields are joined before matching, which is safe precisely because terms are whitespace-split
 * and so can never contain the separator - a term cannot span two fields. Joining also absorbs
 * null, undefined, and numeric fields, so callers can pass raw values without coercing them.
 */
export function matchesAllTerms(terms: string[], fields: unknown[]): boolean {
    if (terms.length === 0) return true;

    const haystack = fields.join(' ').toLowerCase();
    return terms.every(term => haystack.includes(term));
}
